/**
 * notebookEventReconciler (PHASE 5)
 * =================================
 * Pure, deterministic reconciliation helpers to prevent UI freezes from:
 * - duplicated events
 * - out-of-order events
 * - terminal state regressions
 *
 * This module is intentionally framework-agnostic (no React).
 */

export type FileLike = Record<string, any>;
export type NotebookLike = Record<string, any>;

const TERMINAL_FILE_STATUSES = new Set([
  'ready',
  'ready_with_warnings',
  'failed',
  'duplicate',
  'blocked_low_quality',
  'ghost',
  'incomplete',
  'unavailable',
]);

function fileIdOf(f: any): string | null {
  const id = f?.fileId ?? f?.id;
  if (id == null) return null;
  return String(id);
}

function parseUpdatedAtMs(v: any): number {
  const ms = Date.parse(String(v || ''));
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Merge file lists with:
 * - stale discard (updatedAt regression)
 * - terminal-state protection (never regress terminal → non-terminal)
 * - preservation of existing files not present in the patch (important for local-only transient state)
 */
export function mergeFilesReconciled(prevFiles: FileLike[] = [], patchFiles: FileLike[] = []): FileLike[] {
  const prevList = Array.isArray(prevFiles) ? prevFiles : [];
  const patchList = Array.isArray(patchFiles) ? patchFiles : [];

  const prevMap = new Map<string, FileLike>();
  for (const f of prevList) {
    const id = fileIdOf(f);
    if (id) prevMap.set(id, f);
  }

  const patchIds = new Set<string>();
  const merged: FileLike[] = [];

  for (const pf of patchList) {
    const id = fileIdOf(pf);
    if (!id) {
      merged.push(pf);
      continue;
    }
    patchIds.add(id);
    const existing = prevMap.get(id);
    if (!existing) {
      merged.push(pf);
      continue;
    }

    const prevUpdatedAt = parseUpdatedAtMs(existing?.updatedAt);
    const nextUpdatedAt = parseUpdatedAtMs(pf?.updatedAt);
    if (prevUpdatedAt && nextUpdatedAt && nextUpdatedAt < prevUpdatedAt) {
      merged.push(existing);
      continue;
    }

    const prevStatus = String(existing?.status || '').toLowerCase();
    const nextStatus = String(pf?.status || '').toLowerCase();
    const nextGhostStatus = String(pf?.ghostStatus || pf?.ghost_status || '').toUpperCase();
    const isGhostTerminal =
      prevStatus === 'ghost' || prevStatus === 'incomplete' || prevStatus === 'unavailable';
    const allowGhostRecovery =
      isGhostTerminal && (nextGhostStatus === 'RECOVERED' || nextGhostStatus === 'NONE');
    if (TERMINAL_FILE_STATUSES.has(prevStatus) && nextStatus && !TERMINAL_FILE_STATUSES.has(nextStatus)) {
      if (!allowGhostRecovery) {
        merged.push(existing);
        continue;
      }
    }

    merged.push({ ...existing, ...pf });
  }

  // Preserve any existing files not present in patch
  for (const f of prevList) {
    const id = fileIdOf(f);
    if (id && !patchIds.has(id)) merged.push(f);
  }

  return merged;
}

/**
 * Apply a notebook patch with safe merging (files reconciled).
 */
export function applyNotebookPatch(prev: NotebookLike, patch: NotebookLike): NotebookLike {
  const prevEpoch = Number(prev?.vaultEpoch || 0);
  const nextEpoch = Number(patch?.vaultEpoch || 0);
  if (nextEpoch && prevEpoch && nextEpoch < prevEpoch) {
    return prev;
  }

  const targetId = patch?.id ?? prev?.id;
  const next: NotebookLike = { ...prev, ...patch, id: targetId };
  if (patch?.files) {
    const eventType = String(patch?._eventType || '').toLowerCase();
    const patchFiles = Array.isArray(patch.files) ? patch.files : [];
    const prevFiles = Array.isArray(prev?.files) ? prev.files : [];
    const hasEmptyPatchFiles = Array.isArray(patch.files) && patchFiles.length === 0;
    if ((eventType === 'notebook:snapshot' || eventType === 'notebook:update') && hasEmptyPatchFiles && prevFiles.length > 0) {
      // Preserve existing files when an update/snapshot omits them or sends an empty list.
      next.files = prevFiles;
    } else if (eventType === 'notebook:snapshot' || eventType === 'notebook:update') {
      next.files = patch.files;
    } else if (prev?.files) {
      next.files = mergeFilesReconciled(prev.files, patch.files);
    } else {
      next.files = patch.files;
    }
  }
  return next;
}

